// i18n area 'scopes' (ENEA-B-R15 «tenant», 2026-10-24): the scope console —
// the tenant unit of TVStor: hierarchical scope with RBAC and delegation,
// capacity quota admitted a priori (NET bytes — ratifica 2026-10-24),
// dedicated gateway addresses, QoS per address, per-tenant report.

export const en = {
  scopes: {
    title: 'Scopes',
    offer:
      'tenant = hierarchical scope with RBAC and delegation, capacity quota admitted a priori, dedicated service addresses per tenant on the gateways, QoS per address, audit per share/store, per-tenant report; no network separation beyond the address.',
    separation:
      "The separation is per service address and per client list/authentication of the shares, not per bind nor firewall: a client that knows another tenant's address is stopped by the share's access lists, not by the address.",
    path: 'Scope',
    id: 'Scope id',
    createdAt: 'Created at',
    name: 'Name',
    parent: 'Parent scope',
    rootChip: 'cluster root',
    quota: 'Capacity quota',
    quotaNone: 'no quota',
    promisedNet: 'Promised (net)',
    promisedRaw: 'Raw footprint',
    promisedRawHint:
      'What the clusters actually reserve for the subtree: size × redundancy weight (copies, or (k+m)/k for erasure coding). The quota counts the NET sum — the capacity the tenant sees.',
    admittable: 'Admittable',
    admittableNone: '— (no quota)',
    counts: 'Resources',
    countsLabel: '{volumes} volumes · {fileShares} shares · {objectStores} stores · {subScopes} sub-scopes',
    barHint:
      'Net promises of the scope and its descendants against the quota: every volume, file share and object store of the subtree counts by its size (shares and stores ride a volume). Admission refuses a priori what would overflow — 422 scope_quota_exceeded with the numbers.',
    barOver: 'over the quota',
    noPermission: 'Your grants do not cover this action here',
    rootImmutable: 'The cluster root is system-managed',
    deleteHint: 'Only an empty scope can be deleted (no resources, no sub-scopes, no assigned gateway addresses).',

    quotaDialog: {
      title: 'Capacity quota of {path}',
      capacity: 'Quota',
      capacityHint:
        "Net bytes: a bare number is GiB, «1.5T» or «1.5 TiB» is TiB; empty = no quota. A volume replicated ×3 weighs its size once — the redundancy is the cluster's business.",
      current: 'Promised now: {net} net ({raw} raw footprint)',
      admittableNow: 'Admittable now: {admittable}',
      invariants:
        "The server checks the headroom invariants: not below the subtree's promises; a child's free headroom never above its parent's; a parent never lowered under a child's. Children's quotas are not reservations.",
      refusals: {
        invalid: 'Not a capacity: use «500», «500G», «1.5T» or a byte count',
        floor: 'The floor is 1 GiB',
        belowPromised: 'Below what the subtree already promises — free promises first'
      },
      submit: 'Save quota'
    },

    createDialog: {
      title: 'Create scope',
      name: 'Name',
      nameHint: 'DNS-1123 label, unique in the parent among scopes and objects',
      parent: 'Parent scope',
      quota: 'Capacity quota (optional)',
      submit: 'Create'
    },

    report: {
      tab: 'Report',
      title: 'Tenant report — {path}',
      hint: 'The scope and its descendants, read from the store and the status rows the renders use — no call to the nodes.',
      generatedAt: 'Generated',
      loading: 'Reading the report…',
      download: 'Download the rows',
      csv: 'CSV',
      jsonl: 'JSONL',
      done: 'Saved {name}',
      quota: 'Quota',
      promised: 'Promised',
      promisedLabel: '{net} net · {raw} raw footprint',
      admittable: 'Admittable',
      used: 'Used (observed)',
      usedUnobserved: '{n} without usage observation',
      counts: 'Counts',
      countsLabel:
        '{volumes} volumes · {fileShares} shares · {objectStores} stores · {buckets} buckets · {subScopes} sub-scopes · {clusters} clusters',
      qosTitle: 'QoS',
      qosLabel: '{fileShares} shares · {objectStores} stores · {gatewayAddresses} gateway addresses with a ceiling',
      auditTitle: 'Audit enabled',
      auditLabel: '{fileShares} shares · {objectStores} stores',

      addresses: {
        title: 'Tenant addresses',
        none: "No dedicated address: the tenant's resources are announced on the common gateway addresses (ceph) or on their own service addresses (multiscsi).",
        address: 'Address',
        kind: 'Kind',
        kinds: {
          gateway: 'gateway (floating)',
          service: 'service (dedicated)'
        },
        cluster: 'Cluster',
        holder: 'Held by',
        scope: 'Assigned to',
        common: 'common',
        qos: 'QoS',
        resource: 'Resource'
      },

      rows: {
        title: 'Resources',
        none: 'No resource with capacity in this subtree.',
        kind: 'Kind',
        kinds: {
          volume: 'volume',
          fileShare: 'file share',
          objectStore: 'object store'
        },
        name: 'Name',
        scope: 'Scope',
        cluster: 'Cluster',
        size: 'Size',
        redundancy: 'Redundancy',
        promisedNet: 'Net',
        promisedRaw: 'Raw',
        used: 'Used',
        qos: 'QoS',
        audit: 'Audit',
        addresses: 'Addresses',
        phase: 'Phase'
      }
    }
  }
}

export const it: typeof en = {
  scopes: {
    title: 'Scope',
    offer:
      "tenant = scope gerarchico con RBAC e deleghe, quota di capacità ammessa a priori, indirizzi di servizio dedicati per tenant sui gateway, QoS per indirizzo, audit per share/store, report per tenant; nessuna separazione di rete oltre l'indirizzo.",
    separation:
      "La separazione è per indirizzo di servizio e per liste di client/autenticazione delle share, non per bind né firewall: un client che conosce l'indirizzo di un altro tenant è fermato dalle liste di accesso della share, non dall'indirizzo.",
    path: 'Scope',
    id: 'Id dello scope',
    createdAt: 'Creato il',
    name: 'Nome',
    parent: 'Scope padre',
    rootChip: 'radice del cluster',
    quota: 'Quota di capacità',
    quotaNone: 'nessuna quota',
    promisedNet: 'Promesso (netto)',
    promisedRaw: 'Impronta grezza',
    promisedRawHint:
      "Ciò che i cluster riservano davvero per il sottoalbero: taglia × peso della ridondanza (copie, o (k+m)/k per l'erasure coding). La quota conta la somma NETTA — la capacità che il tenant vede.",
    admittable: 'Ammissibile',
    admittableNone: '— (nessuna quota)',
    counts: 'Risorse',
    countsLabel: '{volumes} volumi · {fileShares} share · {objectStores} store · {subScopes} sotto-scope',
    barHint:
      "Le promesse nette dello scope e dei suoi discendenti contro la quota: ogni volume, share e store del sottoalbero conta per la sua taglia (share e store ridono un volume). L'ammissione rifiuta a priori ciò che traboccherebbe — 422 scope_quota_exceeded coi numeri.",
    barOver: 'oltre la quota',
    noPermission: 'I tuoi grant non coprono questa azione qui',
    rootImmutable: 'La radice del cluster è gestita dal sistema',
    deleteHint: 'Si cancella solo uno scope vuoto (niente risorse, sotto-scope né indirizzi di gateway assegnati).',

    quotaDialog: {
      title: 'Quota di capacità di {path}',
      capacity: 'Quota',
      capacityHint:
        'Byte netti: un numero nudo è in GiB, «1.5T» o «1.5 TiB» in TiB; vuoto = nessuna quota. Un volume replicato ×3 pesa la sua taglia una volta sola — la ridondanza è affare del cluster.',
      current: 'Promesso ora: {net} netti ({raw} di impronta grezza)',
      admittableNow: 'Ammissibile ora: {admittable}',
      invariants:
        'Il server controlla le invarianti sul margine libero: mai sotto le promesse del sottoalbero; il margine libero di un figlio mai sopra quello del padre; un padre mai abbassato sotto quello di un figlio. Le quote dei figli non sono prenotazioni.',
      refusals: {
        invalid: 'Non è una capacità: usa «500», «500G», «1.5T» o un conteggio di byte',
        floor: 'Il pavimento è 1 GiB',
        belowPromised: 'Sotto ciò che il sottoalbero promette già — libera prima le promesse'
      },
      submit: 'Salva la quota'
    },

    createDialog: {
      title: 'Crea scope',
      name: 'Nome',
      nameHint: 'Etichetta DNS-1123, unica nel padre fra scope e oggetti',
      parent: 'Scope padre',
      quota: 'Quota di capacità (facoltativa)',
      submit: 'Crea'
    },

    report: {
      tab: 'Report',
      title: 'Report del tenant — {path}',
      hint: 'Lo scope e i suoi discendenti, letti dallo store e dalle righe di stato che i render già usano — nessuna chiamata ai nodi.',
      generatedAt: 'Generato',
      loading: 'Lettura del report…',
      download: 'Scarica le righe',
      csv: 'CSV',
      jsonl: 'JSONL',
      done: 'Salvato {name}',
      quota: 'Quota',
      promised: 'Promesso',
      promisedLabel: '{net} netti · {raw} di impronta grezza',
      admittable: 'Ammissibile',
      used: 'Usato (osservato)',
      usedUnobserved: "{n} senza osservazione dell'usato",
      counts: 'Conteggi',
      countsLabel:
        '{volumes} volumi · {fileShares} share · {objectStores} store · {buckets} bucket · {subScopes} sotto-scope · {clusters} cluster',
      qosTitle: 'QoS',
      qosLabel: '{fileShares} share · {objectStores} store · {gatewayAddresses} indirizzi di gateway con un tetto',
      auditTitle: 'Audit abilitato',
      auditLabel: '{fileShares} share · {objectStores} store',

      addresses: {
        title: 'Indirizzi del tenant',
        none: 'Nessun indirizzo dedicato: le risorse del tenant sono annunciate sugli indirizzi comuni dei gateway (ceph) o sui propri indirizzi di servizio (multiscsi).',
        address: 'Indirizzo',
        kind: 'Tipo',
        kinds: {
          gateway: 'gateway (flottante)',
          service: 'servizio (dedicato)'
        },
        cluster: 'Cluster',
        holder: 'Tenuto da',
        scope: 'Assegnato a',
        common: 'comune',
        qos: 'QoS',
        resource: 'Risorsa'
      },

      rows: {
        title: 'Risorse',
        none: 'Nessuna risorsa con capacità in questo sottoalbero.',
        kind: 'Tipo',
        kinds: {
          volume: 'volume',
          fileShare: 'share',
          objectStore: 'object store'
        },
        name: 'Nome',
        scope: 'Scope',
        cluster: 'Cluster',
        size: 'Taglia',
        redundancy: 'Ridondanza',
        promisedNet: 'Netto',
        promisedRaw: 'Grezzo',
        used: 'Usato',
        qos: 'QoS',
        audit: 'Audit',
        addresses: 'Indirizzi',
        phase: 'Fase'
      }
    }
  }
}
